import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { toast } from "@performyard/py-components";

import useToast from "../../../hooks/useToast";

import {
	IntegrationName,
	SendUKGCredentialsRequest,
	UpdateIntegrationSettingsRequest,
	fetchCompanyIntegrationInfo,
	fetchIntegrationSettings,
	fetchUKGIntegrationCredentials,
	sendUKGIntegrationCredentials,
	updateIntegrationSettings,
} from "../../services";

/**
 * Query hook that fetches base integration info based on the company.
 **/
export const useCompanyIntegrationInfo = () => {
	return useQuery({
		queryKey: ["company integration info"],
		queryFn: () => fetchCompanyIntegrationInfo(),
		meta: {
			errorMessage: "There was an error getting the company integration info.",
		},
	});
};

/**
 * Query hook that fetches the company's integration settings for a given integration.
 * Used primarily for the integration settings page.
 **/
export const useCompanyIntegrationSettings = (integration: IntegrationName | null) => {
	return useQuery({
		queryKey: ["integrationSettings", integration],
		queryFn: () => fetchIntegrationSettings(integration),
		meta: {
			errorMessage: "There was an error getting the integration settings.",
		},
	});
};

/**
 * Updates the integration settings for an integration. Optimistically updates the query data.
 * Invalidates the initial fetch query on success.
 * @param queryKey - The query key to optimistically update and invalidate
 * @returns A mutation hook to update integration settings
 **/
export const useUpdateIntegrationSettings = (integration: IntegrationName) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (request: UpdateIntegrationSettingsRequest) => updateIntegrationSettings(request),
		// Optimistic update: https://tanstack.com/query/v4/docs/react/guides/optimistic-updates
		onMutate: async ({ data: { deactivate_users_when_not_present, employee_record_mapping_settings } }) => {
			// Cancel any outgoing re-fetches
			// (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey: ["integrationSettings", integration] });

			// Snapshot the previous value
			const previousValue = queryClient.getQueryData(["integrationSettings", integration]);

			const optimisticValue = {
				...(previousValue as object),
				deactivate_users_when_not_present,
				employee_record_mapping_settings,
			};

			// Optimistically update to the new value
			queryClient.setQueryData(["integrationSettings", integration], () => optimisticValue);

			// Return a context object with the snapshot value
			return { previousValue };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["integrationSettings", integration] });
			toast.success("Integration settings updated successfully");
		},
	});
};

/**
 * Query hook that fetches the integration credentials for UKG Pro.
 **/
export const useUKGIntegrationCredentials = () => {
	return useQuery({
		queryKey: ["ukgIntegrationCredentials"],
		queryFn: () => fetchUKGIntegrationCredentials(),
		meta: {
			errorMessage: "There was an error getting the UKG integration credentials.",
		},
	});
};

/**
 * Sends the integration credentials for UKG Pro. Invalidates the initial fetch query on success.
 **/
export const useSendUKGIntegrationCredentials = () => {
	const queryClient = useQueryClient();
	const toast = useToast();

	return useMutation({
		mutationFn: (sendUKGCredentialsRequest: SendUKGCredentialsRequest) =>
			sendUKGIntegrationCredentials(sendUKGCredentialsRequest),
		onSuccess: (response) => {
			queryClient.invalidateQueries({ queryKey: ["ukgIntegrationCredentials"] });

			// We receive a 200 response even if the credentials are deemed invalid.
			// To differentiate between valid and invalid credentials, we check the response body.
			if (response.success) {
				toast("These credentials have been validated.", { type: "success" });
			} else {
				toast("These credentials may be invalid. Please try again.", { type: "error" });
			}
		},
		onError: () => {
			// If we make it here, there's probably a server error we didn't expect.
			toast("Something went wrong. Please try again.", { type: "error" });
		},
	});
};
